/** CrmLinkEmailModal — the manual email → IOT-deal link dialog (manual-email-to-record-link-spec
 * §3.2/§3.3/§3.5, build plan ML-4). It is the positive twin of `CrmRefuteEvidenceModal`: that one
 * detaches an email from a deal, this one attaches a timeline email — or the current governed
 * snapshot of its thread — to one.
 *
 * ⛔ MOUNT IT AT THE TIMELINE CARD ROOT, NEVER INSIDE THE ROW. Same lifecycle rule (and the same
 * reason) as the refute modal: a successful link invalidates the source timeline, so the row the
 * operator clicked can be refetched, reordered or paged away in the same tick — and a dialog owned by
 * that row unmounts with it, taking the "Thread linked to …" confirmation with it before anyone reads
 * it. `CrmTimeline` therefore holds the `linkFor` state itself.
 *
 * ⛔ NO PREFLIGHT (spec §3.4 rev 3). V1 does not read existing links before submitting and shows no
 * "already linked to another deal" warning: the command is additive and idempotent, and the POST
 * response reports created vs already-linked. A GET here would be a second governed read per open,
 * for a warning that cannot change what the button does.
 *
 * The modal owns no pipeline vocabulary beyond display copy — list name, stage and lifecycle all come
 * from the typed target payload — so a later fundraising provider reuses it unchanged.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { ApiError } from '../../api/client';
import {
  EVIDENCE_LINK_LIST_ID,
  EVIDENCE_LINK_PIPELINE_KIND,
  useEvidenceLinkTargets,
  useLinkEmailToRecord,
  type CrmEntityKind,
} from '../../hooks/useCrm';
import type { CrmEvidenceLinkTarget } from '../../types';
import { fmtShortDate } from './crmFormat';
import styles from './crm.module.css';
import link from './CrmLinkEmailModal.module.css';

/** The exact helper text under a checked **Link full thread** (spec §3.2). Exported so the test pins
 *  the shipped string rather than a second copy of it. */
export const THREAD_HELPER_TEXT =
  'Links all messages currently in this thread that you can access. '
  + 'Future replies can inherit this deal through normal thread matching.';

/** The house debounce (`CrmMergeModal`, `CrmDealMergeModal`, `CrmRefCellEditor`, `CrmListGrid`).
 *  The target search is a governed, audited read — one per keystroke is not acceptable. */
const DEBOUNCE_MS = 250;

/** A refusal the operator can read, WITHOUT the failed gate.
 *
 * Spec §3.1/§10: a metadata-visible but content-unauthorized source must not disclose which gate said
 * no, and caller-facing errors carry no source/target identifiers. So the three governed shapes get
 * fixed copy; only an unclassified failure is allowed to surface the server's own words. `retryable`
 * is false for all three: 403 and 404 will not change by pressing the button again, and retrying a
 * 409 with the same stale `expected_thread_id` fails identically. */
export function linkErrorMessage(e: unknown): { text: string; retryable: boolean } {
  const status = e instanceof ApiError ? e.status : (e as { status?: number } | null)?.status;
  if (status === 403) {
    return { text: 'You are not allowed to link this email to that deal.', retryable: false };
  }
  if (status === 404) {
    return { text: 'That email is no longer available to link.', retryable: false };
  }
  if (status === 409) {
    return {
      text: 'This thread changed while this dialog was open. '
        + 'Close it and reopen the email to link the current thread.',
      retryable: false,
    };
  }
  const detail = (e as { detail?: { detail?: string | { message?: string } } } | null)?.detail?.detail;
  if (typeof detail === 'string' && detail) return { text: detail, retryable: true };
  const message = (detail as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string' && message) return { text: message, retryable: true };
  return { text: 'Could not link that email.', retryable: true };
}

/** RFC-4122 v4, from the platform when it has one. The server validates the shape and USES it as the
 *  operation id (spec §5.2), so a weak or repeated value is a correctness problem, not cosmetics. */
export function newIdempotencyKey(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])';

export interface CrmLinkEmailModalProps {
  /** Canonical `crm_email_message.id` of the row the operator invoked Link on. */
  messageId: number;
  subject: string | null;
  /** The row's `occurred_at` — the sent date in the "which email am I linking" line. */
  occurredAt: string | null;
  /** Canonical thread id from the rendered row; null disables thread scope entirely (spec §3.2).
   *  It also rides the request as `expected_thread_id`, so the server refuses a stale snapshot. */
  threadId: string | null;
  /** The profile hosting the timeline — only so the mutation can invalidate that timeline's keys. */
  sourceKind: CrmEntityKind;
  sourceId: number;
  onClose: () => void;
}

export function CrmLinkEmailModal({
  messageId, subject, occurredAt, threadId, sourceKind, sourceId, onClose,
}: CrmLinkEmailModalProps) {
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [entryId, setEntryId] = useState<number | null>(null);
  const canThread = threadId != null && threadId !== '';
  const [fullThread, setFullThread] = useState(true);
  const [done, setDone] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDq(q), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const targets = useEvidenceLinkTargets(dq);
  const mutation = useLinkEmailToRecord();

  const rows: CrmEvidenceLinkTarget[] = targets.data?.targets ?? [];
  // DERIVED, never mirrored into state — which is how a selection the operator can no longer see is
  // clamped for free (the defect `CrmMergeModal`'s review finding 10 caught: editing the query drops
  // the chosen deal out of the results while a `survivorId` state keeps pointing at it, leaving a
  // confirm button armed for a deal that is not on screen). Here `selected` goes null with its row,
  // and the primary action disables with it.
  const selected = rows.find((r) => r.entry_id === entryId) ?? null;

  /** `operation_id` IS this key server-side (spec §5.2), so it must be STABLE across retries of the
   *  same decision and DIFFERENT for a different one. Keyed on target+scope: pressing "Try again"
   *  after a 500 replays the same operation (idempotent, so a write the client never saw is not
   *  duplicated), while changing the deal or the scope after an error starts a new operation rather
   *  than filing rows for two targets under one id. */
  const keyRef = useRef<{ sig: string; key: string } | null>(null);
  const idempotencyKeyFor = (sig: string): string => {
    if (!keyRef.current || keyRef.current.sig !== sig) {
      keyRef.current = { sig, key: newIdempotencyKey() };
    }
    return keyRef.current.key;
  };

  const scope: 'message' | 'thread' = canThread && fullThread ? 'thread' : 'message';
  const err = mutation.isError ? linkErrorMessage(mutation.error) : null;

  const submit = () => {
    // Double-submit protection, belt and braces with the button's own `disabled`: a second Enter
    // while the first POST is in flight would be a second operation id for one human decision.
    if (mutation.isPending || done || selected == null) return;
    mutation.mutate({
      messageId,
      target: {
        pipeline_kind: EVIDENCE_LINK_PIPELINE_KIND,
        list_id: selected.list_id ?? EVIDENCE_LINK_LIST_ID,
        entry_id: selected.entry_id,
      },
      scope,
      expectedThreadId: threadId ?? null,
      idempotencyKey: idempotencyKeyFor(`${selected.entry_id}:${scope}`),
      sourceKind,
      sourceId,
    }, {
      onSuccess: (d) => {
        // The SERVER's scope, not the requested one: a message whose thread vanished between render
        // and write degrades to message scope, and the confirmation must say what happened.
        setDone(d.scope === 'thread'
          ? `Thread linked to ${selected.name} — ${d.created} messages added, `
            + `${d.already_linked} already linked.`
          : `Email linked to ${selected.name}.`);
      },
    });
  };

  /** Escape closes; Tab cycles inside the dialog (spec §3.2 — focus is trapped). */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !cardRef.current) return;
    const items = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  };

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div
        ref={cardRef}
        className={`${styles.modalCard} ${link.card}`}
        role="dialog"
        aria-modal="true"
        aria-label="Link email to a deal"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <h3 className={styles.modalTitle}>Link email to a deal</h3>

        <div className={styles.evRefuteSubject}>{subject || '(no subject)'}</div>
        <div className={link.sent}>Sent {fmtShortDate(occurredAt)}</div>

        {done ? (
          <div className={styles.evRefuteDone} role="status">{done}</div>
        ) : (
          <>
            <label className={styles.mergeLabel} htmlFor="crm-link-email-q">Search IOT deals</label>
            <input
              id="crm-link-email-q"
              type="text"
              autoFocus
              className={styles.modalInput}
              placeholder="Company name or codename…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <div className={styles.mergeResults} data-testid="crm-link-email-results">
              {targets.isLoading && <div className={styles.mergeEmpty}>Searching…</div>}
              {!targets.isLoading && rows.length === 0 && (
                <div className={styles.mergeEmpty}>
                  {dq.trim() ? 'No matching deals.' : 'No deals to pick from.'}
                </div>
              )}
              {rows.map((r) => (
                <button
                  key={`${r.list_id}-${r.entry_id}`}
                  type="button"
                  className={r.entry_id === entryId ? styles.mergeResultOn : styles.mergeResult}
                  aria-pressed={r.entry_id === entryId}
                  onClick={() => setEntryId(r.entry_id)}
                  // Enter selects the FOCUSED result (spec §3.2). A focused <button> fires its own
                  // onClick anyway; this makes the contract explicit rather than incidental.
                  onKeyDown={(e) => { if (e.key === 'Enter') setEntryId(r.entry_id); }}
                >
                  <span className={styles.mergeResultName}>{r.name}</span>
                  {r.lifecycle_status === 'proposed' && (
                    <span className={link.proposed}>Proposed</span>
                  )}
                  <span className={styles.mergeResultMeta}>
                    {r.stage || 'no stage'} · {r.list_name}
                  </span>
                </button>
              ))}
            </div>

            <label className={styles.modalCheck}>
              <input
                type="checkbox"
                checked={canThread && fullThread}
                disabled={!canThread}
                onChange={(e) => setFullThread(e.target.checked)}
              />
              Link full thread
            </label>
            {canThread && fullThread && <div className={link.helper}>{THREAD_HELPER_TEXT}</div>}
            {!canThread && (
              <div className={link.helper}>
                This email is not part of a thread, so only this message will be linked.
              </div>
            )}
          </>
        )}

        {err && <div className={styles.modalError} role="alert">{err.text}</div>}

        <div className={styles.modalActions}>
          <button type="button" className={styles.gridMiniBtn} onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              type="button"
              className={styles.gridPageBtn}
              disabled={selected == null || mutation.isPending}
              onClick={submit}
            >
              {mutation.isPending ? 'Linking…' : (err?.retryable ? 'Try again' : 'Link to deal')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CrmLinkEmailModal;
